import React, { useState, useEffect } from 'react';
import { FaBullhorn, FaChartLine, FaDollarSign, FaEye, FaMousePointer, FaShoppingCart, FaSync, FaCrown, FaFilter } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import EmptyState from '../components/Common/EmptyState';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';
import UpgradePrompt from '../components/Common/UpgradePrompt';
import MetricCard from '../components/Dashboard/MetricCard';

const AmazonAds = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all'); // all, enabled, paused
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const isPro = user?.subscription?.plan === 'pro';

  useEffect(() => {
    if (isPro) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [filter, isPro]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        ...dateRange,
        ...(filter !== 'all' && { status: filter })
      };
      
      const [campaignsRes, statsRes] = await Promise.all([
        api.get('/amazon-ads/campaigns', { params }),
        api.get('/amazon-ads/stats', { params: dateRange })
      ]);

      setCampaigns(campaignsRes.data.data || []);
      setStats(statsRes.data.data || null);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError('');
      setSuccess('');

      await api.post('/amazon-ads/sync');
      
      setSuccess('Campaigns synchronized successfully');
      loadData();

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync campaigns');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleCampaign = async (campaignId, currentState) => {
    try {
      const newState = currentState === 'enabled' ? 'paused' : 'enabled';
      await api.put(`/amazon-ads/campaigns/${campaignId}`, { state: newState });
      
      setSuccess(`Campaign ${newState === 'enabled' ? 'enabled' : 'paused'} successfully`);
      loadData();

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update campaign');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getPerformanceBadge = (acos) => {
    if (acos < 15) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Excellent</span>;
    } else if (acos < 25) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Good</span>;
    } else if (acos < 35) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Fair</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Poor</span>;
    }
  };

  if (!isPro) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Amazon Ads</h1>
          <div className="flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg">
            <FaCrown className="mr-2" />
            Pro Feature
          </div>
        </div>
        <UpgradePrompt
          title="Unlock Amazon Ads Management"
          description="Track and optimize your Amazon PPC campaigns with real-time metrics, ACOS analysis, and performance insights"
          currentPlan="starter"
        />
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading campaigns..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Amazon Ads</h1>
          <p className="text-gray-600 mt-1">Manage and optimize your PPC campaigns</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <FaSync className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Campaigns'}
        </button>
      </div>

      {/* Messages */}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <MetricCard
            title="Total Spend"
            value={formatCurrency(stats.total_spend)}
            icon={<FaDollarSign />}
            color="red"
            subtitle={`${stats.campaigns_count} campaigns`}
          />
          <MetricCard
            title="Total Sales"
            value={formatCurrency(stats.total_sales)}
            icon={<FaShoppingCart />}
            color="green"
            subtitle={`${stats.orders_count} orders`}
          />
          <MetricCard
            title="Average ACOS"
            value={`${stats.avg_acos.toFixed(2)}%`}
            icon={<FaChartLine />}
            color={stats.avg_acos < 25 ? 'green' : stats.avg_acos < 35 ? 'orange' : 'red'}
            subtitle="Ad Cost of Sale"
          />
          <MetricCard
            title="Total Clicks"
            value={stats.total_clicks.toLocaleString()}
            icon={<FaMousePointer />}
            color="blue"
            subtitle={`${stats.total_impressions.toLocaleString()} impressions`}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaFilter className="inline mr-1" />
              Status
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Campaigns</option>
              <option value="enabled">Enabled Only</option>
              <option value="paused">Paused Only</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={loadData}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      {campaigns.length === 0 ? (
        <EmptyState
          icon={<FaBullhorn className="text-6xl text-gray-400" />}
          title="No campaigns found"
          description="Sync your Amazon Ads campaigns to see performance metrics"
          actionLabel="Sync Campaigns"
          onAction={handleSync}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Spend
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACOS
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clicks
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CTR
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => {
                  const acos = campaign.metrics?.acos || 0;
                  const ctr = campaign.metrics?.ctr || 0;
                  
                  return (
                    <tr key={campaign._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {campaign.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            ID: {campaign.campaign_id}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(campaign.metrics?.spend || 0)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="text-sm font-medium text-green-600">
                          {formatCurrency(campaign.metrics?.sales || 0)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {campaign.metrics?.orders || 0} orders
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="text-sm font-bold text-gray-900">
                          {acos.toFixed(2)}%
                        </p>
                        {getPerformanceBadge(acos)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {campaign.metrics?.clicks || 0}
                        </p>
                        <p className="text-xs text-gray-500">
                          {campaign.metrics?.impressions || 0} imp
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {ctr.toFixed(2)}%
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          campaign.state === 'enabled'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.state}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleToggleCampaign(campaign._id, campaign.state)}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            campaign.state === 'enabled'
                              ? 'bg-gray-600 text-white hover:bg-gray-700'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {campaign.state === 'enabled' ? 'Pause' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Performance Tips */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Performance Tips</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="font-medium text-green-900 mb-1">✅ Good ACOS: &lt; 25%</p>
            <p className="text-sm text-green-800">Your ads are profitable and efficient</p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg">
            <p className="font-medium text-yellow-900 mb-1">⚠️ Fair ACOS: 25-35%</p>
            <p className="text-sm text-yellow-800">Consider optimizing keywords and bids</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg">
            <p className="font-medium text-red-900 mb-1">❌ Poor ACOS: &gt; 35%</p>
            <p className="text-sm text-red-800">Pause or restructure these campaigns</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="font-medium text-blue-900 mb-1">💡 CTR Target: &gt; 0.5%</p>
            <p className="text-sm text-blue-800">Higher CTR means better ad relevance</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmazonAds;
